import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, authApi } from '@/lib/api';
import { Trash2, Plus, Save, Loader2, Info } from 'lucide-react';
import MiniMapEditor from '@/components/MiniMapEditor';

const FACILITY_TYPES = ['toilet', 'prayer_room', 'first_aid', 'parking', 'entrance', 'info', 'food', 'shopping', 'stadium'];
const PROP_TYPES = ['tree', 'bush', 'palm', 'lantern', 'bench', 'flag', 'sign', 'banner', 'sculpture', 'custom_icon'];
const EVENT_TYPES = ['ceremony', 'workshop', 'demo', 'performance', 'talk', 'competition', 'exhibition', 'meeting'];
const STRUCTURE_TYPES = [
  'khemah_pempamer', 'khemah_utama', 'khemah_jamuan', 'plot_pbt', 'khemah_jualan_tanaman',
  'khemah_makan', 'my_kiosk', 'holding_vvip', 'khemah_kkm', 'khemah_media',
  'khemah_urus_setia', 'khemah_katering', 'khemah_kaji_selidik',
  'pentas_utama', 'food_truck', 'supa_dupa', 'khemah_rahmah', 'small_booth',
];

function authAxios() {
  return axios.create({
    baseURL: API_URL,
    headers: {
      Authorization: `Bearer ${authApi.getToken()}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
}

export default function LayoutEditor() {
  const [tab, setTab] = useState('booths');
  const [booths, setBooths] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [props, setProps] = useState([]);
  const [events, setEvents] = useState([]);
  const [landmarks, setLandmarks] = useState([]);
  const [users, setUsers] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [assignForm, setAssignForm] = useState({ user_id: '', company_id: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [flash, setFlash] = useState(null);

  const notify = useCallback((msg, isError = false) => {
    setFlash({ msg, isError });
    setTimeout(() => setFlash(null), 2200);
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, f, p, e, cs, lm] = await Promise.all([
        axios.get(`${API_URL}/booths`),
        axios.get(`${API_URL}/facilities`),
        axios.get(`${API_URL}/props`),
        axios.get(`${API_URL}/events`),
        axios.get(`${API_URL}/companies`),
        axios.get(`${API_URL}/landmarks`),
      ]);
      setBooths(b.data.data || []);
      setFacilities(f.data.data || []);
      setProps(p.data.data || []);
      setEvents(e.data.data || []);
      setAllCompanies(cs.data.data || []);
      setLandmarks(lm.data.data || []);
    } catch (e) {
      console.error(e);
      notify('Failed to load data', true);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ============ BOOTHS ============
  const updateBoothField = (id, field, value) => {
    setBooths((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));
  };
  const saveBooth = async (booth) => {
    setSaving(`booth-${booth.id}`);
    try {
      await authAxios().patch(`/admin/booths/${booth.id}/position`, {
        position_x: Number(booth.position_x),
        position_z: Number(booth.position_z),
        rotation: Number(booth.rotation || 0),
        width: Number(booth.width || 5),
        depth: Number(booth.depth || 5),
        height: Number(booth.height || 3),
        structure_type: booth.structure_type,
        color: booth.color,
        status: booth.status,
        company_id: booth.company_id ? Number(booth.company_id) : null,
      });
      notify(`Booth ${booth.booth_number} updated`);
    } catch (e) {
      notify(e.response?.data?.message || 'Save failed', true);
    } finally {
      setSaving(null);
    }
  };

  // ============ FACILITIES ============
  const updateFacilityField = (id, field, value) => {
    setFacilities((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
  };
  const saveFacility = async (facility) => {
    setSaving(`fac-${facility.id}`);
    try {
      await authAxios().patch(`/admin/facilities/${facility.id}`, {
        name: facility.name,
        type: facility.type,
        icon: facility.icon,
        position_x: Number(facility.position_x),
        position_z: Number(facility.position_z),
      });
      notify(`${facility.name} updated`);
    } catch (e) {
      notify(e.response?.data?.message || 'Save failed', true);
    } finally {
      setSaving(null);
    }
  };
  const deleteFacility = async (id) => {
    if (!window.confirm('Delete this facility?')) return;
    try {
      await authAxios().delete(`/admin/facilities/${id}`);
      setFacilities((prev) => prev.filter((f) => f.id !== id));
      notify('Facility deleted');
    } catch (e) {
      notify(e.response?.data?.message || 'Delete failed', true);
    }
  };
  const addFacility = async () => {
    try {
      const res = await authAxios().post('/admin/facilities', {
        name: 'New Facility',
        type: 'toilet',
        icon: '🚻',
        position_x: 0,
        position_z: 0,
      });
      setFacilities((prev) => [...prev, res.data.data]);
      notify('Facility added — remember to save position');
    } catch (e) {
      notify(e.response?.data?.message || 'Add failed', true);
    }
  };

  // ============ PROPS ============
  const updatePropField = (id, field, value) => {
    setProps((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  };
  const saveProp = async (prop) => {
    setSaving(`prop-${prop.id}`);
    try {
      await authAxios().patch(`/admin/props/${prop.id}`, {
        type: prop.type,
        label: prop.label,
        icon: prop.icon,
        position_x: Number(prop.position_x),
        position_z: Number(prop.position_z),
        rotation: Number(prop.rotation || 0),
        scale: Number(prop.scale || 1),
        color: prop.color,
      });
      notify(`Prop #${prop.id} updated`);
    } catch (e) {
      notify(e.response?.data?.message || 'Save failed', true);
    } finally {
      setSaving(null);
    }
  };
  const deleteProp = async (id) => {
    if (!window.confirm('Delete this prop?')) return;
    try {
      await authAxios().delete(`/admin/props/${id}`);
      setProps((prev) => prev.filter((p) => p.id !== id));
      notify('Prop deleted');
    } catch (e) {
      notify(e.response?.data?.message || 'Delete failed', true);
    }
  };
  const addProp = async (type = 'tree') => {
    try {
      const res = await authAxios().post('/admin/props', {
        type,
        label: type,
        icon: type === 'custom_icon' ? '📍' : null,
        position_x: 0,
        position_z: 0,
        scale: 1,
      });
      setProps((prev) => [...prev, res.data.data]);
      notify(`${type} added`);
    } catch (e) {
      notify(e.response?.data?.message || 'Add failed', true);
    }
  };

  // ============ EVENTS ============
  const updateEventField = (id, field, value) => {
    setEvents((prev) => prev.map((ev) => (ev.id === id ? { ...ev, [field]: value } : ev)));
  };
  const saveEvent = async (ev) => {
    setSaving(`event-${ev.id}`);
    try {
      const payload = {
        title: ev.title,
        type: ev.type,
        start_time: toBackendDateTime(ev.start_time),
        end_time: toBackendDateTime(ev.end_time),
        description: ev.description || '',
        location: ev.location || '',
        booth_id: ev.booth_id || null,
        is_featured: !!ev.is_featured,
      };
      await authAxios().patch(`/admin/events/${ev.id}`, payload);
      notify(`Event "${ev.title}" updated`);
    } catch (e) {
      notify(e.response?.data?.message || 'Save failed', true);
    } finally {
      setSaving(null);
    }
  };
  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await authAxios().delete(`/admin/events/${id}`);
      setEvents((prev) => prev.filter((ev) => ev.id !== id));
      notify('Event deleted');
    } catch (e) {
      notify(e.response?.data?.message || 'Delete failed', true);
    }
  };
  // ============ LANDMARKS ============
  const saveLandmark = async (lm) => {
    setSaving(`landmark-${lm.id}`);
    try {
      await authAxios().patch(`/admin/landmarks/${lm.id}`, {
        position_x: Number(lm.position_x),
        position_z: Number(lm.position_z),
        rotation: Number(lm.rotation || 0),
        scale: Number(lm.scale || 1),
        width: Number(lm.width || 20),
        depth: Number(lm.depth || 20),
        height: Number(lm.height || 6),
        color: lm.color,
        label: lm.label,
      });
      notify(`Landmark ${lm.label || lm.type} updated`);
    } catch (e) {
      notify(e.response?.data?.message || 'Save failed', true);
    } finally {
      setSaving(null);
    }
  };

  const createLandmark = async (type) => {
    // Sensible defaults per landmark type
    const defaults = {
      stadium:   { width: 50,  depth: 40, height: 15, color: '#c0522d', label: 'Stadium' },
      carpark:   { width: 80,  depth: 40, height: 0.2, color: '#3a3a3a', label: 'Parkir' },
      tree_line: { width: 60,  depth: 12, height: 4, color: '#166534', label: 'Tree Line' },
      archway:   { width: 10,  depth: 3,  height: 9,  color: '#eab308', label: 'Entrance' },
      palm:      { width: 4,   depth: 4,  height: 7,  color: '#84cc16', label: 'Palm' },
      tree:      { width: 4,   depth: 4,  height: 5,  color: '#166534', label: 'Tree' },
      bush:      { width: 2,   depth: 2,  height: 1,  color: '#22c55e', label: 'Bush' },
      building:  { width: 12,  depth: 12, height: 8,  color: '#94a3b8', label: 'Building' },
    };
    const d = defaults[type] || defaults.tree;
    try {
      const res = await authAxios().post('/admin/landmarks', {
        type,
        position_x: 0, position_z: 0,
        ...d,
      });
      setLandmarks((prev) => [...prev, res.data.data]);
      notify(`${d.label} added at (0,0) — drag to position`);
    } catch (e) {
      notify(e.response?.data?.message || 'Create failed', true);
    }
  };

  const deleteLandmark = async (id) => {
    if (!window.confirm('Delete this landmark?')) return;
    try {
      await authAxios().delete(`/admin/landmarks/${id}`);
      setLandmarks((prev) => prev.filter((l) => l.id !== id));
      notify('Landmark deleted');
    } catch (e) {
      notify(e.response?.data?.message || 'Delete failed', true);
    }
  };

  const addEvent = async () => {
    try {
      const now = new Date();
      const later = new Date(now.getTime() + 60 * 60 * 1000);
      const res = await authAxios().post('/admin/events', {
        title: 'New Event',
        type: 'workshop',
        start_time: toBackendDateTime(now.toISOString()),
        end_time: toBackendDateTime(later.toISOString()),
        description: '',
      });
      setEvents((prev) => [...prev, res.data.data]);
      notify('Event added — edit details and Save');
    } catch (e) {
      notify(e.response?.data?.message || 'Add failed', true);
    }
  };

  // ============ EXHIBITOR ACCESS ============
  const loadUsers = useCallback(async () => {
    try {
      const res = await authAxios().get('/admin/users');
      setUsers(res.data.data.users || []);
      setAllCompanies(res.data.data.companies || []);
    } catch (e) {
      notify(e.response?.data?.message || 'Load users failed', true);
    }
  }, [notify]);

  useEffect(() => { if (tab === 'exhibitors') loadUsers(); }, [tab, loadUsers]);

  const assignExhibitor = async () => {
    if (!assignForm.user_id || !assignForm.company_id) { notify('Pick a user AND a company', true); return; }
    try {
      await authAxios().post('/admin/exhibitors', {
        user_id: Number(assignForm.user_id),
        company_id: Number(assignForm.company_id),
      });
      notify('Exhibitor access granted');
      setAssignForm({ user_id: '', company_id: '' });
      loadUsers();
    } catch (e) {
      notify(e.response?.data?.message || 'Assign failed', true);
    }
  };

  const revokeExhibitor = async (userId, companyId) => {
    if (!window.confirm('Revoke exhibitor access?')) return;
    try {
      await authAxios().delete(`/admin/exhibitors/${userId}/${companyId}`);
      notify('Access revoked');
      loadUsers();
    } catch (e) {
      notify(e.response?.data?.message || 'Revoke failed', true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="layout-editor">
      {flash && (
        <div className={`fixed top-24 right-6 z-50 px-4 py-2 rounded-lg shadow-2xl text-sm font-semibold ${flash.isError ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`} data-testid="editor-flash">
          {flash.msg}
        </div>
      )}

      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
        <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-blue-200">
          <div className="font-bold">Layout Editor</div>
          <div>Coordinate system: <b>X</b> = east/west (−100 to +100), <b>Z</b> = north/south (event area: −30 to +75; carpark: below −50). Click <b>Save</b> to persist and refresh the map view.</div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-800 overflow-x-auto">
        {[
          { key: 'minimap', label: '🗺️ Mini Map' },
          { key: 'booths', label: `Booths (${booths.length})` },
          { key: 'facilities', label: `Facilities (${facilities.length})` },
          { key: 'props', label: `Props (${props.length})` },
          { key: 'events', label: `Events (${events.length})` },
          { key: 'exhibitors', label: '👥 Exhibitor Access' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
              tab === key ? 'text-emerald-400 border-emerald-500' : 'text-slate-400 border-transparent hover:text-white'
            }`}
            data-testid={`editor-tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ============ MINI MAP TAB ============ */}
      {tab === 'minimap' && (
        <MiniMapEditor
          booths={booths}
          facilities={facilities}
          propItems={props}
          landmarks={landmarks}
          onSaveBooth={async (b) => {
            await saveBooth(b);
            setBooths((prev) => prev.map((x) => (x.id === b.id ? { ...x, position_x: b.position_x, position_z: b.position_z } : x)));
          }}
          onSaveFacility={async (f) => {
            await saveFacility(f);
            setFacilities((prev) => prev.map((x) => (x.id === f.id ? { ...x, position_x: f.position_x, position_z: f.position_z } : x)));
          }}
          onSaveProp={async (p) => {
            await saveProp(p);
            setProps((prev) => prev.map((x) => (x.id === p.id ? { ...x, position_x: p.position_x, position_z: p.position_z } : x)));
          }}
          onSaveLandmark={async (lm) => {
            await saveLandmark(lm);
            // Merge every editable field back into state (not just position)
            setLandmarks((prev) => prev.map((x) => (x.id === lm.id ? { ...x, ...lm } : x)));
          }}
          onCreateLandmark={createLandmark}
          onDeleteLandmark={deleteLandmark}
        />
      )}

      {/* ============ BOOTHS TAB ============ */}
      {tab === 'booths' && (
        <div className="overflow-x-auto rounded-xl border border-slate-800">
          <table className="w-full text-xs">
            <thead className="bg-slate-800/70 text-slate-400 uppercase text-[10px]">
              <tr>
                <th className="text-left p-2">#</th>
                <th className="text-left p-2">Booth</th>
                <th className="text-left p-2">Company</th>
                <th className="text-left p-2">Structure</th>
                <th className="text-left p-2">X</th>
                <th className="text-left p-2">Z</th>
                <th className="text-left p-2">Rot°</th>
                <th className="text-left p-2">W</th>
                <th className="text-left p-2">D</th>
                <th className="text-left p-2">H</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Color</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {booths.map((b) => (
                <tr key={b.id} className="hover:bg-slate-800/40" data-testid={`edit-booth-row-${b.id}`}>
                  <td className="p-2 text-slate-500">{b.id}</td>
                  <td className="p-2 text-white font-mono font-bold">{b.booth_number}</td>
                  <td className="p-2 min-w-[170px]">
                    <select
                      value={b.company_id || ''}
                      onChange={(e) => updateBoothField(b.id, 'company_id', e.target.value)}
                      className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-0.5 w-[170px]"
                      data-testid={`edit-booth-company-${b.id}`}
                    >
                      <option value="">— none —</option>
                      {allCompanies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2">
                    <select value={b.structure_type || ''} onChange={(e) => updateBoothField(b.id, 'structure_type', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-0.5 w-[130px]">
                      {STRUCTURE_TYPES.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </td>
                  <NumericCell value={b.position_x} onChange={(v) => updateBoothField(b.id, 'position_x', v)} />
                  <NumericCell value={b.position_z} onChange={(v) => updateBoothField(b.id, 'position_z', v)} />
                  <NumericCell value={b.rotation} onChange={(v) => updateBoothField(b.id, 'rotation', v)} />
                  <NumericCell value={b.width} onChange={(v) => updateBoothField(b.id, 'width', v)} />
                  <NumericCell value={b.depth} onChange={(v) => updateBoothField(b.id, 'depth', v)} />
                  <NumericCell value={b.height} onChange={(v) => updateBoothField(b.id, 'height', v)} />
                  <td className="p-2">
                    <select value={b.status} onChange={(e) => updateBoothField(b.id, 'status', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-0.5">
                      <option value="occupied">occupied</option>
                      <option value="available">available</option>
                      <option value="reserved">reserved</option>
                    </select>
                  </td>
                  <td className="p-2">
                    <input type="color" value={b.color || '#3b82f6'} onChange={(e) => updateBoothField(b.id, 'color', e.target.value)} className="w-8 h-6 bg-transparent border border-slate-700 rounded cursor-pointer" />
                  </td>
                  <td className="p-2">
                    <button
                      onClick={() => saveBooth(b)}
                      disabled={saving === `booth-${b.id}`}
                      className="px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold disabled:opacity-50 flex items-center gap-1"
                      data-testid={`save-booth-${b.id}`}
                    >
                      {saving === `booth-${b.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ============ FACILITIES TAB ============ */}
      {tab === 'facilities' && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs" data-testid="facilities-readonly-notice">
            Admins can edit facility details/positions but cannot add or delete facilities. Structural changes require developer access.
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-xs">
              <thead className="bg-slate-800/70 text-slate-400 uppercase text-[10px]">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Icon</th>
                  <th className="text-left p-2">X</th>
                  <th className="text-left p-2">Z</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {facilities.map((f) => (
                  <tr key={f.id} className="hover:bg-slate-800/40" data-testid={`edit-facility-row-${f.id}`}>
                    <td className="p-2 text-slate-500">{f.id}</td>
                    <td className="p-2">
                      <input value={f.name || ''} onChange={(e) => updateFacilityField(f.id, 'name', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-2 py-1 w-full min-w-[160px]" />
                    </td>
                    <td className="p-2">
                      <select value={f.type} onChange={(e) => updateFacilityField(f.id, 'type', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-1">
                        {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="p-2">
                      <input value={f.icon || ''} onChange={(e) => updateFacilityField(f.id, 'icon', e.target.value)} maxLength={4} className="bg-slate-900 border border-slate-700 text-white text-lg rounded px-1 py-1 w-14 text-center" />
                    </td>
                    <NumericCell value={f.position_x} onChange={(v) => updateFacilityField(f.id, 'position_x', v)} />
                    <NumericCell value={f.position_z} onChange={(v) => updateFacilityField(f.id, 'position_z', v)} />
                    <td className="p-2 flex gap-1">
                      <button onClick={() => saveFacility(f)} disabled={saving === `fac-${f.id}`} className="px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold disabled:opacity-50" data-testid={`save-facility-${f.id}`}>
                        {saving === `fac-${f.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ============ PROPS TAB ============ */}
      {tab === 'props' && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs" data-testid="props-readonly-notice">
            Admins can edit prop positions/scale/color but cannot add or delete props.
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-xs">
              <thead className="bg-slate-800/70 text-slate-400 uppercase text-[10px]">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Label</th>
                  <th className="text-left p-2">Icon</th>
                  <th className="text-left p-2">X</th>
                  <th className="text-left p-2">Z</th>
                  <th className="text-left p-2">Rot°</th>
                  <th className="text-left p-2">Scale</th>
                  <th className="text-left p-2">Color</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {props.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-800/40" data-testid={`edit-prop-row-${p.id}`}>
                    <td className="p-2 text-slate-500">{p.id}</td>
                    <td className="p-2">
                      <select value={p.type} onChange={(e) => updatePropField(p.id, 'type', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-1">
                        {PROP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </td>
                    <td className="p-2">
                      <input value={p.label || ''} onChange={(e) => updatePropField(p.id, 'label', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-2 py-1 min-w-[100px]" />
                    </td>
                    <td className="p-2">
                      <input value={p.icon || ''} onChange={(e) => updatePropField(p.id, 'icon', e.target.value)} maxLength={4} className="bg-slate-900 border border-slate-700 text-white text-lg rounded px-1 py-1 w-14 text-center" />
                    </td>
                    <NumericCell value={p.position_x} onChange={(v) => updatePropField(p.id, 'position_x', v)} />
                    <NumericCell value={p.position_z} onChange={(v) => updatePropField(p.id, 'position_z', v)} />
                    <NumericCell value={p.rotation} onChange={(v) => updatePropField(p.id, 'rotation', v)} />
                    <NumericCell value={p.scale} onChange={(v) => updatePropField(p.id, 'scale', v)} step={0.1} />
                    <td className="p-2">
                      <input type="color" value={p.color || '#16a34a'} onChange={(e) => updatePropField(p.id, 'color', e.target.value)} className="w-8 h-6 bg-transparent border border-slate-700 rounded cursor-pointer" />
                    </td>
                    <td className="p-2 flex gap-1">
                      <button onClick={() => saveProp(p)} disabled={saving === `prop-${p.id}`} className="px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold disabled:opacity-50" data-testid={`save-prop-${p.id}`}>
                        {saving === `prop-${p.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      </button>
                    </td>
                  </tr>
                ))}
                {props.length === 0 && (
                  <tr><td colSpan={10} className="p-6 text-center text-slate-500">No props defined. Structural props are seeded by developers.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* ============ EVENTS TAB ============ */}
      {tab === 'events' && (
        <div className="space-y-3">
          <button onClick={addEvent} className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5" data-testid="add-event-btn">
            <Plus className="w-4 h-4" /> Add Event
          </button>
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-xs">
              <thead className="bg-slate-800/70 text-slate-400 uppercase text-[10px]">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Title</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Start</th>
                  <th className="text-left p-2">End</th>
                  <th className="text-left p-2">Location</th>
                  <th className="text-left p-2">Booth</th>
                  <th className="text-left p-2">Featured</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {events.map((ev) => (
                  <tr key={ev.id} className="hover:bg-slate-800/40 align-top" data-testid={`edit-event-row-${ev.id}`}>
                    <td className="p-2 text-slate-500">{ev.id}</td>
                    <td className="p-2">
                      <input value={ev.title || ''} onChange={(e) => updateEventField(ev.id, 'title', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-2 py-1 min-w-[180px] w-full" />
                    </td>
                    <td className="p-2">
                      <select value={ev.type || 'workshop'} onChange={(e) => updateEventField(ev.id, 'type', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-1">
                        {EVENT_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                      </select>
                    </td>
                    <td className="p-2">
                      <input type="datetime-local" value={toDateTimeLocal(ev.start_time)} onChange={(e) => updateEventField(ev.id, 'start_time', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-1" />
                    </td>
                    <td className="p-2">
                      <input type="datetime-local" value={toDateTimeLocal(ev.end_time)} onChange={(e) => updateEventField(ev.id, 'end_time', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-1" />
                    </td>
                    <td className="p-2">
                      <input value={ev.location || ''} onChange={(e) => updateEventField(ev.id, 'location', e.target.value)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-2 py-1 min-w-[110px]" />
                    </td>
                    <td className="p-2">
                      <select value={ev.booth_id || ''} onChange={(e) => updateEventField(ev.id, 'booth_id', e.target.value || null)} className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-1 max-w-[120px]">
                        <option value="">— none —</option>
                        {booths.map((b) => (<option key={b.id} value={b.id}>{b.booth_number}</option>))}
                      </select>
                    </td>
                    <td className="p-2 text-center">
                      <input type="checkbox" checked={!!ev.is_featured} onChange={(e) => updateEventField(ev.id, 'is_featured', e.target.checked)} className="w-4 h-4 accent-emerald-500" />
                    </td>
                    <td className="p-2 flex gap-1">
                      <button onClick={() => saveEvent(ev)} disabled={saving === `event-${ev.id}`} className="px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-[11px] font-bold disabled:opacity-50" data-testid={`save-event-${ev.id}`}>
                        {saving === `event-${ev.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      </button>
                      <button onClick={() => deleteEvent(ev.id)} className="px-2 py-1 rounded bg-red-500/80 hover:bg-red-600 text-white" data-testid={`delete-event-${ev.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr><td colSpan={9} className="p-6 text-center text-slate-500">No events yet. Click "Add Event" to create one.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* ============ EXHIBITOR ACCESS TAB ============ */}
      {tab === 'exhibitors' && (
        <div className="space-y-4" data-testid="exhibitor-access-tab">
          {/* Assign form */}
          <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest mb-1 block">User</label>
              <select
                value={assignForm.user_id}
                onChange={(e) => setAssignForm((p) => ({ ...p, user_id: e.target.value }))}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs"
                data-testid="assign-user-select"
              >
                <option value="">— pick a user —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} · {u.email} {u.roles?.length ? `[${u.roles.join(',')}]` : ''}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="text-slate-400 text-[10px] uppercase font-bold tracking-widest mb-1 block">Company / Booth</label>
              <select
                value={assignForm.company_id}
                onChange={(e) => setAssignForm((p) => ({ ...p, company_id: e.target.value }))}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs"
                data-testid="assign-company-select"
              >
                <option value="">— pick a company —</option>
                {allCompanies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.user_id ? ' · (already linked)' : ''}</option>
                ))}
              </select>
            </div>
            <button
              onClick={assignExhibitor}
              className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1.5"
              data-testid="assign-exhibitor-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Grant Exhibitor Access
            </button>
          </div>

          {/* Currently linked exhibitors */}
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-xs">
              <thead className="bg-slate-800/70 text-slate-400 uppercase text-[10px]">
                <tr>
                  <th className="text-left p-2">Company</th>
                  <th className="text-left p-2">Linked User</th>
                  <th className="text-left p-2">Email</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {allCompanies.filter((c) => c.user_id).map((c) => {
                  const u = users.find((x) => x.id === c.user_id);
                  return (
                    <tr key={c.id} className="hover:bg-slate-800/40" data-testid={`exhibitor-link-${c.id}`}>
                      <td className="p-2 text-white font-bold">{c.name}</td>
                      <td className="p-2 text-slate-300">{u?.name || `user #${c.user_id}`}</td>
                      <td className="p-2 text-slate-400">{u?.email || '—'}</td>
                      <td className="p-2">
                        <button
                          onClick={() => revokeExhibitor(c.user_id, c.id)}
                          className="px-2 py-1 rounded bg-red-500/80 hover:bg-red-600 text-white text-[11px] font-bold flex items-center gap-1"
                          data-testid={`revoke-exhibitor-${c.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Revoke
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {allCompanies.filter((c) => c.user_id).length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-slate-500">No exhibitors linked yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
function toDateTimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convert datetime-local or ISO string to Laravel-friendly `YYYY-MM-DD HH:MM:SS`
function toBackendDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function NumericCell({ value, onChange, step = 1 }) {
  return (
    <td className="p-2">
      <input
        type="number"
        step={step}
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-900 border border-slate-700 text-white text-[11px] rounded px-1 py-1 w-16 text-right"
      />
    </td>
  );
}
